<script>
  import { createEventDispatcher, onDestroy, tick } from 'svelte';
  import { prepareGatewayLaunch, reconcileRequirement, resumeSigning } from './agreementApi.js';
  import { parseGatewayLaunchUrl } from './gatewaySigning.js';

  export let requirement = null;
  export let launchUrl = '';
  export let open = false;
  export let returnFocus = null;
  const dispatch = createEventDispatcher();
  const POLL_INTERVAL_MS = 4000;
  const MAX_POLLS = 30;
  let dialog, closeButton, launchFrame, pollTimer;
  let session = 0;
  let safeUrl = '', error = '', checking = false, pollCount = 0, wasOpen = false, pollingStopped = false;
  const finalizationFailureMessage = 'Para\u0161as egzistuoja, ta\u010diau jo kvalifikuotumo patikra nepavyko. Automatiniai bandymai sustabdyti. U\u017edarykite lang\u0105 ir per\u017ei\u016br\u0117kite b\u016bsen\u0105 profilyje.';

  $: if (open && !wasOpen) { wasOpen = true; begin(); }
  $: if (!open && wasOpen) { wasOpen = false; teardown(); }
  $: finalizationFailed = requirement?.status === 'finalization_failed';
  function requirementId() { return requirement?.public_id || requirement?.id || ''; }

  async function begin() {
    const currentSession = ++session;
    if (requirement?.status === 'finalization_failed') {
      pollingStopped = true;
      error = finalizationFailureMessage;
      await tick();
      if (dialog && !dialog.open) dialog.showModal();
      await tick(); closeButton?.focus();
      return;
    }
    const parsed = parseGatewayLaunchUrl(launchUrl, window.location.origin);
    if (!parsed) error = 'Gautas nesaugus Dokobit pasirašymo adresas.';
    await tick();
    if (dialog && !dialog.open) dialog.showModal();
    await tick(); closeButton?.focus();
    if (!parsed) return;
    try {
      // The authenticated POST establishes a short-lived HttpOnly launch
      // cookie. The provider capability never appears in a response body.
      await prepareGatewayLaunch(parsed.href);
      if (!open || !wasOpen || currentSession !== session) return;
      safeUrl = parsed.href;
      window.addEventListener('message', handleLaunchMessage);
      schedulePoll();
    } catch (_) {
      if (currentSession !== session || !open) return;
      safeUrl = '';
      error = 'Dokobit pasirašymo lango paruošti nepavyko. Uždarykite langą ir bandykite dar kartą.';
    }
  }

  function handleLaunchMessage(event) {
    if (event.origin !== window.location.origin || event.source !== launchFrame?.contentWindow) return;
    const data = event.data;
    if (!data || data.type !== 'dokobit_gateway_result' || !['success', 'error'].includes(data.outcome)) return;
    if (pollingStopped) return;
    if (data.outcome === 'error') {
      stopPolling('Dokobit pranešė apie klaidą. Automatinis tikrinimas sustabdytas. Galite atnaujinti būseną rankiniu būdu.');
      refreshStatus(true);
    } else refreshStatus();
  }
  function stopPolling(message = '') {
    pollingStopped = true;
    clearTimeout(pollTimer);
    if (message) error = message;
  }
  function schedulePoll() {
    clearTimeout(pollTimer);
    if (!open || !safeUrl || pollingStopped) return;
    if (pollCount >= MAX_POLLS) {
      stopPolling('Automatinis tikrinimas baigtas. Galite atnaujinti būseną rankiniu būdu arba tęsti vėliau.');
      return;
    }
    const currentSession = session;
    pollTimer = setTimeout(async () => {
      await refreshStatus();
      if (currentSession === session) schedulePoll();
    }, POLL_INTERVAL_MS);
  }
  async function refreshStatus(manual = false) {
    const id = requirementId();
    if (!id || checking || !open) return;
    if (!manual && (pollingStopped || pollCount >= MAX_POLLS)) return;
    const currentSession = session;
    checking = true; pollCount += 1;
    try {
      const current = await reconcileRequirement(id);
      if (currentSession !== session || !open) return;
      requirement = current;
      dispatch('refresh', { requirement: current });
      if (['assigned', 'cancelled', 'declined', 'expired', 'provider_failed', 'storage_failed', 'withdrawn'].includes(current?.status)) {
        stopPolling('Pasirašymo bandymas baigtas. Uždarykite langą ir peržiūrėkite būseną profilyje.');
      }
      if (current?.status === 'finalization_failed') {
        pollingStopped = true;
        clearTimeout(pollTimer);
        error = finalizationFailureMessage;
      }
      if (current?.status === 'completed_stored') { dispatch('complete', { requirement: current }); close(); }
    } catch (_) {
      if (currentSession !== session || !open) return;
      stopPolling('Būsenos patikrinti nepavyko. Automatinis tikrinimas sustabdytas. Galite atnaujinti būseną rankiniu būdu.');
    } finally {
      if (currentSession === session) {
        checking = false;
        if (!pollingStopped && pollCount >= MAX_POLLS) stopPolling('Automatinis tikrinimas baigtas. Galite atnaujinti būseną rankiniu būdu arba tęsti vėliau.');
      }
    }
  }
  async function continueTopLevel() {
    const id = requirementId();
    if (!id || requirement?.can_resume !== true) return;
    const popup = window.open('about:blank', '_blank', 'popup');
    if (!popup) { error = 'Naršyklė užblokavo naują langą.'; return; }
    popup.opener = null;
    try {
      const result = await resumeSigning(id, 'top_level');
      const parsed = parseGatewayLaunchUrl(result?.launch_url, window.location.origin);
      if (result?.mode !== 'gateway_top_level' || !parsed) throw new Error('invalid_top_level_launch');
      await prepareGatewayLaunch(parsed.href);
      popup.location.replace(parsed.href);
      close();
    } catch (_) {
      popup.close();
      error = 'Tęsti naujame lange nepavyko.';
    }
  }
  function close() { open = false; dispatch('close'); }
  function teardown() {
    session += 1;
    clearTimeout(pollTimer); pollTimer = null; window.removeEventListener('message', handleLaunchMessage); safeUrl = ''; launchUrl = ''; error = ''; checking = false; pollCount = 0; pollingStopped = false;
    if (dialog?.open) dialog.close();
    tick().then(() => returnFocus?.focus());
  }
  function trapFocus(event) {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = dialog.querySelectorAll('button:not([disabled]),iframe');
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  onDestroy(() => {
    teardown();
  });
</script>

<dialog bind:this={dialog} class="gateway-dialog" aria-labelledby="gateway-title" on:cancel|preventDefault={close} on:keydown={trapFocus}>
  <section class="gateway-shell">
    <header><div><h2 id="gateway-title">Pasirašyti Dokobit</h2><p>{requirement?.title || requirement?.version_title || 'Paslaugų teikimo sutartis'}</p></div><button bind:this={closeButton} type="button" class="close" aria-label="Uždaryti pasirašymo langą" on:click={close}>×</button></header>
    <p class="guidance">{finalizationFailed ? finalizationFailureMessage : 'Peržiūrėkite dokumentą ir atlikite Dokobit rodomus veiksmus. Šį langą galite uždaryti ir tęsti vėliau.'}</p>
    {#if error}<div class="error" role="alert">{error}</div>{/if}
    {#if safeUrl}<iframe bind:this={launchFrame} title="Dokobit dokumento pasirašymas" src={safeUrl} referrerpolicy="no-referrer" allow="clipboard-read; clipboard-write"></iframe>{/if}
    <footer>
      <span aria-live="polite">{checking ? 'Tikrinama pasirašymo būsena…' : ((finalizationFailed || pollingStopped) ? 'Automatinis tikrinimas sustabdytas.' : (safeUrl ? 'Būsena tikrinama automatiškai.' : 'Ruošiamas pasirašymo langas…'))}</span>
      {#if !finalizationFailed && requirement?.can_resume === true}<button type="button" on:click={continueTopLevel}>Tęsti naujame lange</button>{/if}
      {#if !finalizationFailed && pollingStopped}<button type="button" class="refresh" disabled={checking || !safeUrl} on:click={() => refreshStatus(true)}>Atnaujinti būseną</button>{/if}
      <button type="button" on:click={close}>Uždaryti</button>
    </footer>
  </section>
</dialog>

<style>
  dialog{border:0;border-radius:10px;padding:0;width:min(960px,calc(100% - 24px));height:min(860px,calc(100dvh - 24px));max-width:none;max-height:none;color:#333;box-shadow:0 18px 48px rgba(0,0,0,.3);overflow:hidden}dialog::backdrop{background:rgba(0,0,0,.55)}.gateway-shell{box-sizing:border-box;height:100%;min-height:0;display:flex;flex-direction:column;background:#fff}header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:16px 18px 10px;border-bottom:1px solid #e5e7eb}h2{margin:0;color:#14695b;font-size:22px}header p{margin:4px 0 0;line-height:1.4}.close{flex:0 0 44px;width:44px;height:44px;border:0;border-radius:8px;background:transparent;color:#333;font-size:28px;cursor:pointer}.guidance{margin:0;padding:10px 18px;background:#fef3c7;color:#854d0e;line-height:1.4}.error{margin:12px 18px 0;padding:10px;border-radius:8px;background:#fee2e2;color:#b91c1c}iframe{display:block;box-sizing:border-box;min-height:320px;width:100%;height:100%;flex:1 1 auto;border:0;background:#f8fafc}footer{display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-wrap:wrap;padding:10px 18px;border-top:1px solid #e5e7eb}footer span{margin-right:auto;color:#555}footer button{min-height:44px;padding:8px 14px;border:1px solid #14695b;border-radius:8px;background:white;color:#14695b;font-weight:600}.refresh{background:#fab45a;color:#111;border-color:#333}button:focus,iframe:focus{outline:2px solid #14695b;outline-offset:2px}button:disabled{opacity:.6}@media(max-width:600px){dialog{width:100%;height:100dvh;border-radius:0}.guidance{font-size:14px}footer{align-items:stretch}footer span{width:100%;margin:0}footer button{flex:1 1 140px}header{padding:12px}.guidance{padding:9px 12px}}
</style>
